import React, { useEffect, useState } from "react";
import "./Legislators.css";
import { Typography, Tabs, TabList, Tab, Button, Stack, Box, IconButton, Input } from "@mui/joy";
import AppsIcon from "@mui/icons-material/Apps";
import ListIcon from "@mui/icons-material/List";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import MemberList from "../../components/MemberList/MemberList";
import Loading from "../Loading";
import FindRepresentative from "../../components/FindRepresentative/FindRepresentative";
import SEOHead from "../../components/SEOHead/SEOHead";
import seoConfig from "../../utils/seoConfig";
import CapitolImg from "../../tim-mossholder-LseXGRuWthI-unsplash.jpg";

function Legislators() {
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [tab, setTab] = useState("senator");
  const [display, setDisplay] = useState("cards");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.title = seoConfig.pages.legislators.title;
    if (localStorage.getItem(tab)) {
      setLoading(true);
      setMembers([]);
      const memberArr = JSON.parse(localStorage.getItem(tab)).sort((a, b) =>
        a.state.localeCompare(b.state)
      );
      setMembers(memberArr);
      setLoading(false);
    } else {
      setLoading(true);
      setMembers([]);
      fetch(`/api/member/${tab}`)
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        })
        .then((data) => {
          const arr = Array.isArray(data) ? data : [];
          const sorted = arr.sort((a, b) => a.state.localeCompare(b.state));
          setMembers(sorted);
          localStorage.setItem(tab, JSON.stringify(sorted));
          setLoading(false);
        })
        .catch((err) => {
          console.error("Failed to load members:", err);
          setLoading(false);
        });
    }
  }, [tab]);

  return (
    <>
      <SEOHead
        title={seoConfig.pages.legislators.title}
        description={seoConfig.pages.legislators.description}
        keywords={seoConfig.pages.legislators.keywords}
        path={seoConfig.pages.legislators.path}
        structuredData={[seoConfig.getBreadcrumbs("/legislators", "Legislators")]}
      />

      <Box
        sx={{
          position: "relative",
          minHeight: "100vh",
          backgroundColor: "#003049",
          backgroundImage: `linear-gradient(rgba(0,48,73,0.8), rgba(0,48,73,0.8)), url(${CapitolImg})`,
          backgroundSize: "100% 100%, cover",
          backgroundPosition: "0 0, center top",
          backgroundAttachment: "scroll, fixed",
        }}
      >
        <Stack direction="column" sx={{ position: "relative", zIndex: 1 }}>

          {/* Hero */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              px: { xs: "1.25rem", md: "2rem" },
              pt: { xs: "2.5rem", md: "3rem" },
              pb: { xs: "2rem", md: "2.5rem" },
            }}
          >
            <Box sx={{ maxWidth: "1200px", width: "100%" }}>
              <Stack spacing={4}>
                <Stack spacing={2}>
                  <Typography
                    component="p"
                    sx={{
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "var(--ce-dark-eyebrow)",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    119th Congress
                  </Typography>
                  <Typography
                    level="h1"
                    sx={{
                      color: "white",
                      fontSize: { xs: "2.25rem", md: "3.75rem" },
                      fontWeight: 700,
                      lineHeight: 1.2,
                    }}
                  >
                    Legislators
                  </Typography>
                  <Typography
                    sx={{
                      color: "white",
                      fontSize: "1.125rem",
                      lineHeight: 1.65,
                      maxWidth: "640px",
                    }}
                  >
                    Browse all current members of the Senate and House. Find who represents your district and contact them directly.
                  </Typography>
                </Stack>

                {/* Info banner */}
                <Box
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "4px",
                    padding: "24px",
                    display: "flex",
                    alignItems: "center",
                    gap: "40px",
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    sx={{
                      flex: 1,
                      minWidth: "240px",
                      fontSize: "18px",
                      lineHeight: 1.55,
                      color: "#32383e",
                    }}
                  >
                    Fewer than 20% of Americans know their congressional district and nearly a third don&apos;t know their state officials beyond the governor. Learn your district representation by entering your address into our search feature.
                  </Typography>
                  <Button
                    size="lg"
                    onClick={() => setOpen(true)}
                    sx={{
                      backgroundColor: "var(--ce-accent)",
                      color: "white",
                      px: 3,
                      fontSize: "16px",
                      fontWeight: 600,
                      borderRadius: "6px",
                      minHeight: "48px",
                      flexShrink: 0,
                      transition: "background-color 0.15s ease",
                      "&:hover": { backgroundColor: "var(--ce-accent-hover)" },
                      "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                    }}
                  >
                    Find my representatives
                  </Button>
                </Box>
              </Stack>
            </Box>
          </Box>

          {/* Controls + Grid */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              px: { xs: "1.25rem", md: "2rem" },
              py: { xs: "2rem", md: "2.5rem" },
            }}
          >
            <Box sx={{ maxWidth: "1200px", width: "100%" }}>
              <Stack spacing={3}>

                {/* Chamber tabs */}
                <Tabs
                  value={tab}
                  onChange={(_, newValue) => { setTab(newValue); setSearch(""); }}
                  sx={{ background: "transparent" }}
                >
                  <TabList
                    sx={{
                      background: "transparent",
                      borderBottom: "1px solid rgba(255,255,255,0.15)",
                      gap: 0,
                      "--List-padding": "0px",
                      "--ListItem-radius": "0px",
                      "--Tab-indicatorSize": "0px",
                    }}
                  >
                    {[
                      { value: "senator", label: "Senators" },
                      { value: "representative", label: "House Representatives" },
                    ].map(({ value, label }) => (
                      <Tab
                        key={value}
                        value={value}
                        sx={{
                          px: "20px",
                          py: 1.5,
                          fontSize: "15px",
                          fontWeight: 500,
                          color: "rgba(255,255,255,0.6)",
                          background: "transparent",
                          borderRadius: 0,
                          "&:hover": {
                            color: "white",
                            background: "rgba(255,255,255,0.06)",
                          },
                          "&[aria-selected='true']": {
                            color: "white",
                            fontWeight: 600,
                            backgroundColor: "var(--ce-accent)",
                            borderRadius: "4px 4px 0 0",
                            borderBottom: "1px solid var(--ce-accent)",
                            marginBottom: "-1px",
                            zIndex: 1,
                          },
                          transition: "color 0.15s ease, background-color 0.15s ease",
                          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                        }}
                      >
                        {label}
                      </Tab>
                    ))}
                  </TabList>
                </Tabs>

                {/* View toggle (left) + Search (right) */}
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  sx={{ alignItems: { sm: "center" } }}
                >
                  <Box sx={{ display: "flex" }}>
                    <IconButton
                      variant={display === "cards" ? "solid" : "plain"}
                      color="primary"
                      onClick={() => setDisplay("cards")}
                      aria-label="Card view"
                      aria-pressed={display === "cards"}
                      sx={{
                        "--Icon-color": display === "cards" ? "white" : "#0075b2",
                        borderRadius: "6px 0 0 6px",
                        width: 40,
                        height: 40,
                        border: "1px solid",
                        borderColor: display === "cards" ? "#0075b2" : "#dfe4ea",
                        backgroundColor: display === "cards" ? "#0075b2 !important" : "white",
                        marginRight: "-1px",
                        zIndex: display === "cards" ? 1 : 0,
                        "&:hover": {
                          "--Icon-color": display === "cards" ? "white" : "#005c8e",
                          backgroundColor: display === "cards" ? "#005c8e" : "#f4f6f8",
                        },
                      }}
                    >
                      <AppsIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton
                      variant={display === "list" ? "solid" : "plain"}
                      color="primary"
                      onClick={() => setDisplay("list")}
                      aria-label="List view"
                      aria-pressed={display === "list"}
                      sx={{
                        "--Icon-color": display === "list" ? "white" : "#0075b2",
                        borderRadius: "0 6px 6px 0",
                        width: 40,
                        height: 40,
                        border: "1px solid",
                        borderColor: display === "list" ? "#0075b2" : "#dfe4ea",
                        backgroundColor: display === "list" ? "#0075b2 !important" : "white",
                        zIndex: display === "list" ? 1 : 0,
                        "&:hover": {
                          "--Icon-color": display === "list" ? "white" : "#005c8e",
                          backgroundColor: display === "list" ? "#005c8e" : "#f4f6f8",
                        },
                      }}
                    >
                      <ListIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>

                  <Input
                    placeholder="Search by name or state ..."
                    startDecorator={<SearchIcon sx={{ color: "#9ca3af" }} />}
                    endDecorator={
                      search ? (
                        <IconButton
                          size="sm"
                          variant="plain"
                          onClick={() => setSearch("")}
                          aria-label="Clear search"
                          sx={{ color: "#9ca3af" }}
                        >
                          <ClearIcon />
                        </IconButton>
                      ) : null
                    }
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{
                      flex: 1,
                      backgroundColor: "white",
                      border: "1px solid #dfe4ea",
                      borderRadius: "6px",
                      fontSize: "16px",
                      "--Input-focusedHighlight": "var(--ce-accent)",
                      "&:focus-within": {
                        borderColor: "var(--ce-accent)",
                        boxShadow: "0 0 0 3px rgba(0,117,178,0.15)",
                      },
                    }}
                  />
                </Stack>

                {/* Member grid */}
                {loading && members.length === 0 ? (
                  <Loading />
                ) : (
                  <MemberList members={members} display={display} search={search} tab={tab} />
                )}
              </Stack>
            </Box>
          </Box>
        </Stack>
      </Box>

      <FindRepresentative open={open} setOpen={setOpen} />
    </>
  );
}

export default Legislators;
